import { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from 'next-themes';
import { Camera, Loader2, Moon, Sun, Check, SunDim } from 'lucide-react';
import { toast } from 'sonner';
import { COLOR_THEMES, applyColorTheme, applyBrightness, getSavedBrightness, getSavedThemeId, saveDarkModeLocal } from '@/hooks/useColorTheme';
import { useCompanyId } from '@/hooks/useCompanyId';

interface ProfileSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hideDarkMode?: boolean;
}

const ProfileSettingsDialog = ({ open, onOpenChange, hideDarkMode }: ProfileSettingsDialogProps) => {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const { theme, setTheme } = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedThemeId, setSelectedThemeId] = useState(getSavedThemeId);
  const [brightness, setBrightness] = useState(getSavedBrightness);

  const companyId = useCompanyId();
  const ar = lang === 'ar';

  const saveThemeToDb = async (updates: { color_theme?: string; brightness?: number; dark_mode?: boolean }) => {
    if (!user) return;
    await supabase.from('profiles').update(updates).eq('id', user.id);
  };

  const brightnessTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const debouncedSaveBrightness = useCallback((val: number) => {
    clearTimeout(brightnessTimerRef.current);
    brightnessTimerRef.current = setTimeout(() => saveThemeToDb({ brightness: val }), 500);
  }, [user]);

  useEffect(() => {
    if (!open || !user) return;
    setName(user.user_metadata?.name || '');
    setEmail(user.email || '');

    supabase
      .from('profiles')
      .select('avatar_url')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        setAvatarUrl(data?.avatar_url || null);
      });
  }, [open, user]);

  const initials = name
    ? name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
    : (email?.[0] || 'U').toUpperCase();

  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error(ar ? 'حجم الصورة يجب أن يكون أقل من 5 ميجابايت' : 'Image must be smaller than 5MB');
      return;
    }

    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `${companyId || 'default'}/${user.id}/avatar.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(path, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from('avatars').getPublicUrl(path);
      const url = `${data.publicUrl}?t=${Date.now()}`;
      setAvatarUrl(url);

      await supabase
        .from('profiles')
        .update({ avatar_url: url })
        .eq('id', user.id);

      toast.success(ar ? 'تم تحديث الصورة' : 'Avatar updated');
    } catch {
      toast.error(ar ? 'فشل رفع الصورة' : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const isDark = theme === 'dark';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{ar ? 'إعدادات الملف الشخصي' : 'Profile Settings'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Avatar */}
          <div className="flex flex-col items-center gap-3">
            <div className="relative">
              <Avatar className="h-20 w-20 cursor-pointer" onClick={handleAvatarClick}>
                <AvatarImage src={avatarUrl || undefined} alt={name} />
                <AvatarFallback className="text-lg bg-primary text-primary-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <button
                onClick={handleAvatarClick}
                disabled={uploading}
                className="absolute -bottom-1 -end-1 rounded-full bg-primary p-1.5 text-primary-foreground shadow hover:bg-primary/90 transition-colors"
              >
                {uploading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Camera className="h-3.5 w-3.5" />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {ar ? 'انقر لتغيير الصورة (حد 5 ميجابايت)' : 'Click to change photo (max 5MB)'}
            </p>
          </div>

          {/* Name - read only */}
          <div className="space-y-1.5">
            <Label htmlFor="profile-name">{ar ? 'الاسم' : 'Name'}</Label>
            <Input
              id="profile-name"
              value={name}
              readOnly
              disabled
              className="cursor-default"
            />
          </div>

          {/* Email - read only */}
          <div className="space-y-1.5">
            <Label htmlFor="profile-email">{ar ? 'البريد الإلكتروني' : 'Email'}</Label>
            <Input
              id="profile-email"
              type="email"
              value={email}
              readOnly
              disabled
              dir="ltr"
              className="cursor-default"
            />
          </div>

          {!hideDarkMode && (
            <>
              {/* Dark Mode */}
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="flex items-center gap-2">
                  {isDark ? (
                    <Moon className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Sun className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="text-sm font-medium">
                    {ar ? 'المظهر الداكن' : 'Dark Mode'}
                  </span>
                </div>
                <Switch
                  checked={isDark}
                  onCheckedChange={(checked) => {
                    setTheme(checked ? 'dark' : 'light');
                    saveDarkModeLocal(checked);
                    saveThemeToDb({ dark_mode: checked });
                  }}
                />
              </div>

              {/* Brightness */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <SunDim className="h-4 w-4 text-muted-foreground" />
                    {ar ? 'مستوى الإضاءة' : 'Brightness'}
                  </Label>
                  <span className="text-xs text-muted-foreground">
                    {brightness < 35
                      ? (ar ? 'داكن' : 'Dark')
                      : brightness > 65
                      ? (ar ? 'فاتح' : 'Light')
                      : (ar ? 'متوسط' : 'Default')}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Moon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <Slider
                    min={0}
                    max={100}
                    step={5}
                    value={[brightness]}
                    onValueChange={([val]) => {
                      setBrightness(val);
                      applyBrightness(val);
                      debouncedSaveBrightness(val);
                    }}
                    className="flex-1"
                  />
                  <Sun className="h-4 w-4 shrink-0 text-muted-foreground" />
                </div>
              </div>
            </>
          )}

        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileSettingsDialog;
